import { useState, type FormEvent } from 'react';
import { siteInfo } from '@/mocks/buzz';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dagl9ohtto0ceqqnhuhg';

export default function Subscribe() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [emailValue, setEmailValue] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get('phone_alt') as string) || '').trim();
    setStatus('loading');

    if (honeypot) {
      setStatus('success');
      setEmailValue('');
      form.reset();
      return;
    }
    fd.delete('phone_alt');

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        '';
      const combined = (serverMsg || responseText).toLowerCase();

      if (!res.ok || !parsed || (parsed?.code && parsed.code !== 'OK') || combined.includes('spam')) {
        setStatus('error');
        setFormError(serverMsg && combined.includes('spam') ? serverMsg : '订阅失败，请稍后再试或检查邮箱格式。');
        return;
      }
      setStatus('success');
      setEmailValue('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，订阅未完成，请稍后再试。');
    }
  }

  return (
    <section id="subscribe" className="relative scroll-mt-20 overflow-hidden py-16 md:py-24">
      <div className="absolute inset-0 bg-gradient-to-br from-coral-50 via-paper to-leaf-50" />
      <div className="relative mx-auto w-full max-w-3xl px-4 text-center md:px-6">
        <p className="font-display text-3xl text-coral-500 md:text-4xl">订阅每日速览</p>
        <h2 className="mt-2 text-2xl font-black leading-snug md:text-3xl">
          每天一读，从此追热点不掉队
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-ink-soft md:text-base">
          留下常用邮箱，我们会把当日精华快讯合集准时投递给你。随时可退订，绝不打扰。
        </p>

        {status === 'success' ? (
          <div className="mx-auto mt-8 flex max-w-md items-center justify-center gap-3 rounded-full bg-leaf-500 px-6 py-4 text-white">
            <i className="ri-checkbox-circle-line text-xl" />
            <p className="text-sm font-bold">订阅成功，欢迎上车，明天见！</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-readdy-form className="mx-auto mt-8 max-w-xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label htmlFor="sub-email" className="sr-only">
                常用邮箱
              </label>
              <input
                id="sub-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="输入你的常用邮箱"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                className="w-full flex-1 rounded-full border border-ink/10 bg-white px-5 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-coral-400"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="subscribe-phone-alt"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-coral-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-coral-600 disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-mail-send-line" />
                )}
                立即订阅
              </button>
            </div>
            {status === 'error' && (
              <p className="mt-3 text-sm font-medium text-coral-600">{formError}</p>
            )}
            <p className="mt-4 text-xs text-ink-muted">
              订阅即代表同意接收速览邮件，我们不会向第三方共享你的邮箱。
            </p>
          </form>
        )}

        <div className="mt-10 flex flex-col items-center justify-center gap-2 text-xs text-ink-soft sm:flex-row sm:gap-8">
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <i className="ri-mail-line text-leaf-500" /> {siteInfo.email}
          </span>
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <i className="ri-customer-service-2-line text-coral-500" /> 工作日 9:00-18:00 在线
          </span>
        </div>
      </div>
    </section>
  );
}